/**
 * "Help me write": a prompt box that drafts into the composer. Figma node
 * 706:45242 (panel 232:5049). Presentational; the composer owns the request and
 * the result. Nothing here sends.
 */
import { useState } from 'react';
import type { ComposerKind } from './ThreadComposer';
import { Atom } from '../../../design-system/icons';

export interface HelpMeWritePanelProps {
  kind: ComposerKind;
  /** Counter only: a human has typed an amount. The panel never invents one. */
  ratesReady: boolean;
  busy: boolean;
  error?: string | null;
  onWrite: (prompt: string) => void;
  onEditRates: () => void;
  onClose: () => void;
}

export function HelpMeWritePanel({ kind, ratesReady, busy, error, onWrite, onEditRates, onClose }: HelpMeWritePanelProps) {
  const [prompt, setPrompt] = useState('');
  const needsAmount = kind === 'counter' && !ratesReady;
  const canWrite = prompt.trim() !== '' && !busy && !needsAmount;

  return (
    <section
      aria-label="Help me write"
      className="mx-3 mt-3 flex flex-col gap-3 rounded-xl border border-(--border-brand-default) bg-linear-to-b from-[#fdfcff] to-[#eeebfb] p-3"
    >
      <div className="flex items-center justify-between">
        <span className="text-sm/5 text-[#5a07ce]">Help me write</span>
        <button type="button" className="text-sm/5 text-[#5a07ce] hover:underline" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="flex items-end gap-2">
        <textarea
          aria-label="What do you want to say?"
          rows={1}
          value={prompt}
          placeholder="Describe what you want to say"
          onChange={(e) => setPrompt(e.target.value)}
          className="min-h-9 flex-1 resize-none rounded-md border border-border bg-card px-3 py-2 text-sm/5"
        />
        <button
          type="button"
          disabled={!canWrite}
          onClick={() => onWrite(prompt.trim())}
          className="inline-flex h-11 shrink-0 items-center gap-2 rounded-full bg-foreground px-4 text-sm text-background disabled:opacity-50"
        >
          <Atom size={20} aria-hidden />
          {busy ? 'Writing…' : 'Write it'}
        </button>
      </div>
      {needsAmount && (
        <p className="text-xs text-muted-foreground">
          Set your counter amount first.{' '}
          <button type="button" className="underline" onClick={onEditRates}>
            Edit rates
          </button>
        </p>
      )}
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
      <p className="text-xs/4 text-muted-foreground">Grounded in this thread and the campaign brief. Nothing sends until you do.</p>
    </section>
  );
}
